import { Field, Input, Select, enumOptions } from "@dl/web-core-js/ui";
import { useTranslation } from "react-i18next";

/**
 * Descriptor-driven form rendering for the specification tabs.
 *
 * Supplementary alone has thirty-odd fields; writing each as JSX would bury
 * the two facts that matter per field — its type and its key — under repeated
 * markup. A descriptor row keeps each field to one line, which is also what
 * makes the tab easy to check against the Vaadin reference field-for-field.
 */
export type SpecField<T> =
  | { kind: "text"; key: keyof T & string; labelKey: string; fallback: string }
  | { kind: "int" | "number"; key: keyof T & string; labelKey: string; fallback: string }
  | {
      kind: "bool";
      key: keyof T & string;
      labelKey: string;
      fallback: string;
    }
  | {
      kind: "select";
      key: keyof T & string;
      labelKey: string;
      fallback: string;
      options: readonly string[];
    };

export function SpecForm<T extends Record<string, unknown>>({
  fields,
  value,
  onChange,
  readOnly,
}: {
  fields: Array<SpecField<T>>;
  value: T;
  onChange: (next: T) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  const set = (key: string, next: unknown) => onChange({ ...value, [key]: next });

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {fields.map((field) => {
        const label = t(field.labelKey, field.fallback);
        const current = value[field.key];

        if (field.kind === "bool") {
          // Tri-state on purpose: "not specified" is real data for a spec
          // sheet, distinct from an explicit No.
          return (
            <Field key={field.key} label={label} htmlFor={field.key}>
              <Select
                id={field.key}
                disabled={readOnly}
                value={current === true ? "true" : current === false ? "false" : ""}
                placeholder=""
                options={[
                  { value: "true", label: t("value.yes", "Yes") },
                  { value: "false", label: t("value.no", "No") },
                ]}
                onChange={(event) =>
                  set(field.key, event.target.value === "" ? null : event.target.value === "true")
                }
              />
            </Field>
          );
        }

        if (field.kind === "select") {
          return (
            <Field key={field.key} label={label} htmlFor={field.key}>
              <Select
                id={field.key}
                disabled={readOnly}
                value={(current as string | null | undefined) ?? ""}
                placeholder=""
                options={enumOptions(field.options)}
                onChange={(event) => set(field.key, event.target.value || null)}
              />
            </Field>
          );
        }

        if (field.kind === "int" || field.kind === "number") {
          return (
            <Field key={field.key} label={label} htmlFor={field.key}>
              <Input
                id={field.key}
                type="number"
                step={field.kind === "int" ? 1 : "0.01"}
                value={(current as number | null | undefined) ?? ""}
                readOnly={readOnly}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  set(field.key, event.target.value === "" || Number.isNaN(parsed) ? null : parsed);
                }}
              />
            </Field>
          );
        }

        return (
          <Field key={field.key} label={label} htmlFor={field.key}>
            <Input
              id={field.key}
              value={(current as string | null | undefined) ?? ""}
              readOnly={readOnly}
              onChange={(event) => set(field.key, event.target.value || null)}
            />
          </Field>
        );
      })}
    </div>
  );
}
